/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { AbsenceService } from '@absences/services/absence.service';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LocationService } from '@app/shared/services/location.service';
import * as AppointmentActions from '@appointments/store/appointment.actions';
import * as AuthActions from '@auth/store/auth.actions';
import { currentUser, isAuthenticated } from '@auth/store/auth.selectors';
import { State } from '@core/store';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Tour, TourClass } from '@shared/models';
import { DriverTeam } from '@shared/models/driver-team';
import { WorkflowAction } from '@shared/models/workflow-action';
import { TourView } from '@tours/models/tour-view';
import { TourAppointmentsService } from '@tours/services/tour-appointments.service';
import { ToursService } from '@tours/services/tours.service';
import { getCurrentDate, getCurrentTour, getCurrentTourView } from '@tours/store/tours.selectors';
import {
  ConfirmationOverlayComponent
} from '@tours/tours/tour-list/tour-item-menu/confirmation-overlay/confirmation-overlay.component';
import { merge, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as TourActions from './tours.actions';

@Injectable()
export class TourEffects {
  constructor(
    private actions$: Actions<TourActions.TourActions>,
    private toursService: ToursService,
    private tourAppointmentsService: TourAppointmentsService,
    private store: Store<State>,
    private router: Router,
    private snackBarService: MatSnackBar,
    private translateService: TranslateService,
    private absenceService: AbsenceService,
    private locationService: LocationService,
    private dialogService: MatDialog
  ) { }

  
  init$ = createEffect(() => this.actions$.pipe(
    ofType(ROOT_EFFECTS_INIT),
    switchMap(_ => this.store.select(isAuthenticated)),
    map(authenticated => {
      let classes = [];
      if (authenticated) {
        this.store.dispatch(new TourActions.LoadLocations());
        classes = JSON.parse(localStorage.getItem('tourAppointmentClasses'));
      }
      return new TourActions.Init(classes);
    })
  ));

  
  loadTourAppointmentClasses$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.LOGIN_SUCCESS),
    switchMap(() => this.tourAppointmentsService.getTourAppointmentClasses().pipe(
      tap(tourAppointmentClasses => localStorage.setItem('tourAppointmentClasses', JSON.stringify(tourAppointmentClasses))),
      switchMap(tourAppointmentClasses => of(new TourActions.LoadTourAppointmentClassesSuccess(tourAppointmentClasses))),
      catchError(() => of(new TourActions.LoadTourAppointmentClassesFailure()))
    ))
  ));

  
  loadLocationsOnLogin$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.LOGIN_SUCCESS),
    switchMap(_ => {
      return of(new TourActions.LoadLocations());
    }),
  ));

  
  clearLocationsOnLogout$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.LOGOUT),
    switchMap(_ => {
      return of(new TourActions.LoadLocationsSuccess([]));
    }),
  ));

  
  loadLocationsOnAction$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.LOAD_LOCATIONS),
    switchMap(() => this.locationService.getLocations()),
    switchMap(locations => {
        return of(new TourActions.LoadLocationsSuccess(locations));
    }),
    catchError(() => of(new TourActions.LoadLocationsFailure()))
  ));

  
  setCurrentLocationAndLoadTours$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.SET_CURRENT_LOCATION),
    map(_ => new TourActions.LoadTours())
  ));

  
  setCurrentLocationAndLoadAppointments$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.SET_CURRENT_LOCATION),
    map(_ => new AppointmentActions.LoadAppointments())
  ));

  
  setCurrentLocationAndLoadStatistics$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.SET_CURRENT_LOCATION),
    switchMap(_ => this.store.select(getCurrentDate)),
    map(date => new TourActions.LoadMonthlyStatistics(date))
  ));

  
  loadTours$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.LOAD_TOURS),
    withLatestFrom(this.store.select(getCurrentDate)),
    switchMap(([_, date]) => this.toursService.getToursForDay(date).pipe(
      map(tours => new TourActions.LoadToursSuccess(tours)),
      catchError(() => of(new TourActions.LoadToursFailure()))
    ))
  ));

  
  loadToursFailure$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.LOAD_TOURS_FAILURE),
    switchMap(() => this.translateService.get('tours.errors.load').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  setCurrentDate$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.SET_CURRENT_DATE, TourActions.INCREMENT_CURRENT_DATE, TourActions.DECREMENT_CURRENT_DATE),
    withLatestFrom(this.store.select(getCurrentTourView)),
    switchMap(([_, currentView]) => {
      const actions: Action[] = [
        new TourActions.LoadCurrentAbsences(),
        new TourActions.LoadAbsenceSummary()
      ];
      switch (currentView) {
        case TourView.Tours:
          actions.push(new TourActions.LoadTours());
          break;
        case TourView.Appointments:
          actions.push(new AppointmentActions.LoadAppointments());
          break;
      }

      return actions;
    })
  ));

  
  loadTour$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.LOAD_TOUR),
    map(action => action.tourId),
    switchMap(tourId => {
      const getTour = of(tourId);
      const createTour = getTour.pipe(
        filter(id => !id),
        switchMap(() => this.store.select(currentUser)),
        switchMap((manager) => of({
          '@id': null,
          tourType: TourClass.Delivery,
          manager,
          appointment: {
            class: null
          },
          teams: [{
            mainDriver: null,
            vehicle: null,
            additionalDrivers: []
          } as DriverTeam],
          allowPostpone: true
        } as Tour))
      );
      const loadTour = getTour.pipe(
        filter(id => !!id),
        switchMap(id => this.toursService.getTour(id))
      );

      return merge(createTour, loadTour).pipe(
        switchMap(tour => of(new TourActions.LoadTourSuccess(tour))),
        catchError(() => of(new TourActions.LoadTourFailure()))
      );
    })
  ));

  
  loadTourFailure$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.LOAD_TOUR_FAILURE),
    switchMap(() => this.translateService.get('tours.errors.load').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  persistTour$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.PERSIST),
    map(action => action.tour),
    switchMap(tour => {
      const persist = of(tour['@id']);
      const createTour = persist.pipe(
        filter(tourId => !tourId),
        switchMap(() => this.toursService.createTour(tour))
      );
      const updateTour = persist.pipe(
        filter(tourId => !!tourId),
        switchMap(() => this.toursService.updateTour(tour)),
        withLatestFrom(this.store.select(getCurrentTour)),
        switchMap(([updatedTour, originalTour]) => {
          return  this.toursService.handleRemovedDriverTeams(tour.teams, originalTour.teams).pipe(
            map(_ => updatedTour)
          );
        })
      );

      return merge(createTour, updateTour).pipe(
        switchMap(persistedTour => [
          new TourActions.PersistTourSuccess(persistedTour),
          new TourActions.LoadTours()
        ]),
        catchError(() => of(new TourActions.PersistTourFailure()))
      );
    })
  ));

  
  showTourOverview$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.PERSIST_SUCCESS),
    tap(() => this.router.navigate(['tours']))
  ), {dispatch: false});

  
  persistTourFailure$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.PERSIST_FAILURE),
    switchMap(() => this.translateService.get('tours.errors.persist').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  postponeTours$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.POSTPONE_TOURS),
    map(action => action.tourIds),
    switchMap(tours => this.toursService.postponeTours(tours).pipe(
      map(() => new TourActions.PostponeToursSuccess()),
      catchError(() => of(new TourActions.PostponeToursFailure()))
    ))
  ));

  
  postponeToursSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.POSTPONE_TOURS_SUCCESS),
    withLatestFrom(this.store.select(getCurrentDate)),
    switchMap(([_, currentDate]) => [
      new TourActions.LoadTours(),
      new TourActions.LoadMonthlyStatistics(currentDate)
    ])
  ));

  
  postponeToursFailure$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.POSTPONE_TOURS_FAILURE),
    switchMap(() => this.translateService.get('tours.errors.postpone').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  
  updateTourAppointmentSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.UPDATE_TOUR_APPOINTMENT_SUCCESS, TourActions.POSTPONE_TOURS_SUCCESS),
    map(() => new TourActions.LoadTours())
  ));

  
  updateTourAppointment$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.UPDATE_TOUR_APPOINTMENT),
    switchMap(action => this.tourAppointmentsService.updateTourAppointment(action.appointment).pipe(
      switchMap(() => of(new TourActions.UpdateTourAppointmentSuccess())),
      catchError(() => of(new TourActions.UpdateTourAppointmentFailure()))
    ))
  ));

  
  updateTourAppointmentFailure$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.UPDATE_TOUR_APPOINTMENT_FAILURE, TourActions.TOUR_WORKFLOW_ACTION_FAILURE),
    switchMap(() => this.translateService.get('tours.errors.persist').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), {dispatch: false});

  
  executeTourWorkflowAction$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.TOUR_WORKFLOW_ACTION),
    map((action: TourActions.TourWorkflowAction) => action.payload),
    switchMap((workflowAction: WorkflowAction) => {
      if (workflowAction.name === 'delete') {
        return this.toursService.getTour(workflowAction.target.url)
          .pipe(
            switchMap((tour: Tour) => {
              return this.dialogService.open(ConfirmationOverlayComponent, { data: { tour } } as any)
                  .afterClosed();
              }
            ),
            switchMap(confirmation => of({ workflowAction, confirmation }))
          );

      }

      return of({ workflowAction, confirmation: true });
    }),
    switchMap((results: { workflowAction: WorkflowAction, confirmation: boolean }): Observable<Action> => {
      if (!results.confirmation) {
        return of(new TourActions.TourWorkflowActionAborted());
      }

      return this.toursService.executeWorkflowAction(results.workflowAction).pipe(
        withLatestFrom(this.store.select(getCurrentDate)),
        switchMap(([_, currentDate]) => [
          new TourActions.LoadTours(),
          new TourActions.LoadMonthlyStatistics(currentDate)
        ]),
        catchError((error: any) => of(new TourActions.TourWorkflowActionFailure(error.message)))
      );
    })
  ));

  
  loadCurrentAbsences$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.LOAD_CURRENT_ABSENCES),
    withLatestFrom(this.store.select(getCurrentDate)),
    switchMap(([_, date]) => {
      return this.absenceService.getAbsences({
        from: date,
        till: date,
        targetEnabled: true
      }).pipe(
        map((absences) => new TourActions.LoadCurrentAbsencesSuccess(absences)),
        catchError((error: any) => of(new TourActions.LoadCurrentAbsencesFailure(error.message)))
      );
    })
  ));

  
  loadAbsenceSummary$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.LOAD_ABSENCE_SUMMARY),
    withLatestFrom(this.store.select(getCurrentDate)),
    switchMap(([_, date]) => {
      return this.absenceService.getAbsenceSummary(date).pipe(
        map((summary) => new TourActions.LoadAbsenceSummarySuccess(summary)),
        catchError((error: any) => of(new TourActions.LoadAbsenceSummaryFailure(error.message)))
      );
    })
  ));

  
  loadMonthlyStatistics$ = createEffect(() => this.actions$.pipe(
    ofType(TourActions.LOAD_MONTHLY_STATISTICS),
    map(action => action.month),
    switchMap(month => this.toursService.getTourStatistics(month).pipe(
      map(statistics => new TourActions.LoadMonthlyStatisticsSuccess(statistics)),
      catchError(() => of(new TourActions.LoadMonthlyStatisticsFailure()))
    ))
  ));
}
